//Tutorial Modal.tsx - Videos served from the admin-managed API (replaces the old Google Sheets CSV feed)

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../../../../../Assets/styles/LandingStyles/LandingComponentsStyles/Tutorial/TutorialModal.css';
import { ReactComponent as BookSvg } from '../../../../../Assets/svgs/book-open-solid.svg';
import { ReactComponent as GamePadSvg } from '../../../../../Assets/svgs/gamepad-solid.svg';
import { ReactComponent as HandshakeSvg } from '../../../../../Assets/svgs/handshake-angle-solid.svg';
import { ReactComponent as RocketSvg } from '../../../../../Assets/svgs/rocket-solid.svg';
import { useLanguage } from '../../../../../Core/Translations/LanguageContext';
import { LanguageSelector } from '../../../../../Core/Translations/LanguageSelector';
import Config from '../../../../../Core/Config.json';

interface TutorialsModalProps {
    onClose: () => void;
}

// A single video as returned (already localized) by the public tutorials endpoint.
interface TutorialVideo {
    id: string;
    title: string;
    videoUrl: string;
    category: string;
}

type TutorialTab = 'introduction' | 'getting-started' | 'vs-friends' | 'how-to-play';

// Admin-managed tutorials feed (replaces the old Google Sheets CSV).
const API_URL = `${Config.apiBaseUrl}/api/Tutorial/GetActive`;

// The four tabs rendered by this modal. Other categories (e.g. the Beginners-Guide
// standalone videos) are served by the same endpoint but ignored here.
const TUTORIAL_TABS: TutorialTab[] = ['introduction', 'getting-started', 'vs-friends', 'how-to-play'];

export const TutorialsModal: React.FC<TutorialsModalProps> = ({ onClose }) => {
    const { t, language } = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get('tab') as TutorialTab;

    const [activeTab, setActiveTab] = useState<TutorialTab>(
        TUTORIAL_TABS.includes(urlTab) ? urlTab : 'introduction'
    );

    const [videosByTab, setVideosByTab] = useState<Record<string, TutorialVideo[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch videos for the current language. The endpoint returns titles already
    // localized, so we refetch whenever the language changes.
    useEffect(() => {
        const fetchTutorialData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const langCode = language || 'en';
                const response = await fetch(`${API_URL}?lang=${encodeURIComponent(langCode)}`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch tutorials: ${response.statusText}`);
                }

                const data = await response.json();
                const videos: TutorialVideo[] = data?.videos || [];

                // Group videos by tab (only the four tutorial categories are shown here).
                const grouped = videos.reduce((acc, video) => {
                    const tab = (video.category || '').toLowerCase();
                    if (!TUTORIAL_TABS.includes(tab as TutorialTab)) {
                        return acc;
                    }
                    if (!acc[tab]) {
                        acc[tab] = [];
                    }
                    acc[tab].push(video);
                    return acc;
                }, {} as Record<string, TutorialVideo[]>);

                setVideosByTab(grouped);
            } catch (err) {
                console.error('Error fetching tutorial data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load tutorial data');
                setVideosByTab({});
            } finally {
                setIsLoading(false);
            }
        };

        fetchTutorialData();
    }, [language]);

    // Update URL when tab changes
    const handleTabChange = (tab: TutorialTab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    // Sync with browser navigation
    useEffect(() => {
        if (urlTab && urlTab !== activeTab) {
            setActiveTab(urlTab);
        }
    }, [urlTab]);

    // Helper function to extract YouTube video ID from various URL formats
    const extractYouTubeId = (url: string): string => {
        if (url.includes('youtube.com/shorts/')) {
            const idMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/);
            return idMatch ? idMatch[1] : '';
        }

        const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        return match ? match[1] : '';
    };

    // Handler to close modal and clear params
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            setSearchParams({});
            onClose();
        }
    };

    // Render tutorial videos for the current tab in a grid layout
    const renderTutorialVideos = () => {
        if (isLoading) {
            return (
                <div className="tutorials-loading">
                    <div>{t.tutorials.messages.loading}</div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="tutorials-error">
                    <div>{t.tutorials.messages.error} {error}</div>
                    <div>{t.tutorials.messages.fallback}</div>
                </div>
            );
        }

        const videos = videosByTab[activeTab] || [];

        if (videos.length === 0) {
            return (
                <div className="tutorials-empty">
                    <div>{t.tutorials.messages.empty}</div>
                </div>
            );
        }

        return (
            <div className="tutorials-video-grid">
                {videos.map(video => {
                    const videoId = extractYouTubeId(video.videoUrl);

                    return (
                        <div key={video.id} className="tutorials-video-container">
                            <div className="tutorials-video-placeholder">
                                <iframe
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    title={video.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="tutorials-video-iframe"
                                />
                                <div className="tutorials-video-title">{video.title}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="tutorials-modal-overlay" onClick={handleOverlayClick}>
            <div className="tutorials-modal-container tutorials-modal">
                {/* Header container */}
            <div className="tutorials-modal-header">
                <div className="tutorials-modal-title">
                    {t.tutorials.modalTitle}
                    <div style={{ marginLeft: 'auto', marginRight: '30px' }}>
                        <LanguageSelector theme="header" dropdownDirection='down' />
                    </div>

                    <button
                        className="tutorials-close-button"
                        onClick={() => {
                            setSearchParams({});
                            onClose();
                        }}
                    >
                        &#x00D7;
                    </button>
                </div>
            </div>

                <div className="tutorials-content">
                    {/* Sidebar navigation */}
                    <div className="tutorials-sidebar">
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'introduction' ? 'active' : ''}`}
                            onClick={() => handleTabChange('introduction')}>
                            <BookSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.introduction}</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'getting-started' ? 'active' : ''}`}
                            onClick={() => handleTabChange('getting-started')}>
                            <RocketSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.gettingStarted}</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'vs-friends' ? 'active' : ''}`}
                            onClick={() => handleTabChange('vs-friends')}>
                            <HandshakeSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.vsFriends}</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'how-to-play' ? 'active' : ''}`}
                            onClick={() => handleTabChange('how-to-play')}>
                            <GamePadSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.howToPlay}</span>
                        </button>
                    </div>

                    {/* Main content area for videos */}
                    <div className="tutorials-video-content">
                        {renderTutorialVideos()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TutorialsModal;
